import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { getUserPoints, UserPoints } from '../api/usersApi';
import { getUserMatchBets, Match, MatchStatus } from '../api/matchesApi';
import Table from '../components/Table';
import { getUserPointsColumns } from '../utils/userPointsTableUtils';
import MatchCard from '../components/MatchCard';
import { usePlaceMatchBet } from '../hooks/usePlaceMatchBet';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userPoints, setUserPoints] = useState<UserPoints[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const matchRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Use shared hook for placing bets
  const { placeBet } = usePlaceMatchBet({
    onSuccess: (matchId, updatedMatch) => {
      // Update the match in the local state to show the bet
      setMatches(prevMatches => 
        prevMatches.map(match => {
          if (match.id === matchId) {
            return {
              ...match,
              user_bet_home_score: updatedMatch.user_bet_home_score,
              user_bet_away_score: updatedMatch.user_bet_away_score,
              user_bet_winner: updatedMatch.user_bet_winner
            };
          }
          return match;
        })
      );
    },
    onError: (error) => {
      console.error('Error placing/updating bet:', error);
      alert('אירעה שגיאה בהצבת התוצאה. אנא נסה שוב מאוחר יותר.');
    }
  });

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        setLoading(true);
        const matchData = await getUserMatchBets();
        
        if (!Array.isArray(matchData)) {
          setMatches([]);
          return;
        }
  
        const DAY_MS = 24 * 60 * 60 * 1000;
        const now = Date.now();
        const IST_OFFSET_MS = 3 * 60 * 60 * 1000; // UTC+3 (Israel summer)

        // Tomorrow 10:00 AM IST as a UTC timestamp
        const nowInIST = now + IST_OFFSET_MS;
        const istMidnightMs = Math.floor(nowInIST / DAY_MS) * DAY_MS;
        const tomorrowCutoffUTC = istMidnightMs + 34 * 60 * 60 * 1000 - IST_OFFSET_MS;

        const twentyFourHoursAgo = now - DAY_MS;

        const allPastOrLive = matchData
          .filter(m => m.status === MatchStatus.LIVE || m.status === MatchStatus.COMPLETED)
          .sort((a, b) => new Date(b.match_date).getTime() - new Date(a.match_date).getTime()); // newest first

        const allUpcoming = matchData
          .filter(m => m.status === MatchStatus.SCHEDULED)
          .sort((a, b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime()); // soonest first

        // Upcoming games: now → tomorrow 10:00 AM IST
        const upcomingInWindow = allUpcoming.filter(m =>
          new Date(m.match_date).getTime() <= tomorrowCutoffUTC
        );

        // Past games: last 24h, with min-2 fallback from history
        const pastInWindowCount = allPastOrLive.filter(m =>
          new Date(m.match_date).getTime() >= twentyFourHoursAgo
        ).length;
        const pastToShow = allPastOrLive.slice(0, Math.max(pastInWindowCount, 2));

        // Cap total at 8; upcoming has priority, always keep min 2 past
        const pastCount = Math.min(pastToShow.length, Math.max(2, 8 - upcomingInWindow.length));
        const selectedPast = pastToShow.slice(0, pastCount).reverse(); // chronological order

        let finalSelection: Match[] = [...selectedPast, ...upcomingInWindow];

        // Ensure at least 4 games by extending with upcoming beyond the window
        if (finalSelection.length < 4) {
          const upcomingBeyondWindow = allUpcoming.filter(m =>
            new Date(m.match_date).getTime() > tomorrowCutoffUTC
          );
          finalSelection = [
            ...finalSelection,
            ...upcomingBeyondWindow.slice(0, 4 - finalSelection.length),
          ];
        }

        setMatches(finalSelection);
        setError(null);
      } catch (err) {
        setError('Failed to load matches.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchMatches();
  }, []);

  useEffect(() => {
    const fetchUserPoints = async () => {
      try {
        setLoading(true);
        const points = await getUserPoints();
        setUserPoints(points);
        setError(null);
      } catch (err) {
        setError('Failed to load user points');
        console.error('Error fetching user points:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserPoints();
  }, []);

  const columns = getUserPointsColumns();

  // Wrapper to get match from matches array for the hook
  const handlePlaceBet = async (matchId: string, homeScore: string, awayScore: string, winnerId?: number | null) => {
    const match = matches.find(m => m.id === matchId);
    await placeBet(matchId, homeScore, awayScore, winnerId, match);
  };
  return (
    <Layout>
      <div className="container mx-auto">
        {/* <div className="max-w-lg mx-auto mb-6">
          <h1 className="text-2xl font-bold text-white">בית</h1>
          <p className="text-gray-400">ברוך הבא {user?.first_name} {user?.last_name}</p>
        </div> */}
          <div className="space-y-1 mb-2">
            {matches.map(match => (
              <div
                key={match.id}
                ref={el => (matchRefs.current[match.id] = el)}
              >
                <MatchCard 
                  match={match} 
                  onPlaceBet={handlePlaceBet}
                  compact={true}
                />
              </div>
            ))}
          </div>

          <div className="text-center mb-4 pl-7">
            <button
              onClick={() => navigate('/bets/matches')}
              className="text-sm text-stake-blue hover:text-stake-blue/80 transition-colors"
            >
              לכל המשחקים &larr;
            </button>
          </div>

          {error ? (
            <div className="text-stake-red text-center p-4">{error}</div>
          ) : (
            <Table
              className="w-full max-w-lg"
              title='טבלת ניקוד'
              data={userPoints}
              withRanks={true}
              tableFixed
              rowLimit={matches.length >= 8 ? 3 : 5}
              columns={columns}
              keyExtractor={(item) => `${item.first_name}-${item.last_name}`}
              emptyMessage="No user points data available"
              isLoading={loading}
              highlightCondition={(item) => item.id === user?.id}
              legend={[
                { color: 'bg-green-600', label: 'מושלם' },
                { color: 'bg-blue-500', label: 'חלקי' },
                { color: 'bg-gray-500', label: 'כלום' },
              ]}
              compact
            />
          )}
        
      </div>
    </Layout>
  );
};

export default Dashboard; 